import { useState } from 'react';
import { useTransitionGeoSchemeMutation } from '@shared/api';
import { availableActions, type GeoActionSpec, type GeoScheme } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { useAction } from './hooks';
import { ChecksList } from './parts';
import { ErrorText, FieldLabel, Hint, Notice, Row, Stack, TextArea } from './ui';

/**
 * Кнопки переходов статуса ИС по автомату ТЗ §3.4 — только те, что доступны по
 * статусу и правам карточки (`permissions`). Каждое действие подтверждается в
 * диалоге: отклонения требуют причину, передача в ПТО — чистого контроля ГОСТ.
 */
export function SchemeActions({
  scheme,
  actor,
  size = 'md',
}: {
  scheme: GeoScheme;
  /** Показать действия только одной стороны (геодезист / инженер ПТО). */
  actor?: GeoActionSpec['actor'];
  size?: 'sm' | 'md';
}) {
  const [pending, setPending] = useState<GeoActionSpec | null>(null);
  const actions = availableActions(scheme.status, scheme.permissions).filter(
    (a) => !actor || a.actor === actor,
  );
  if (actions.length === 0) return null;
  return (
    <>
      <Row $gap={2}>
        {actions.map((a) => (
          <Button
            key={a.action}
            type="button"
            $variant={a.variant}
            $size={size}
            onClick={() => setPending(a)}
          >
            {a.label}
          </Button>
        ))}
      </Row>
      {pending && (
        <TransitionDialog scheme={scheme} spec={pending} onClose={() => setPending(null)} />
      )}
    </>
  );
}

function TransitionDialog({
  scheme,
  spec,
  onClose,
}: {
  scheme: GeoScheme;
  spec: GeoActionSpec;
  onClose: () => void;
}) {
  const [transition] = useTransitionGeoSchemeMutation();
  const { run, busy, error } = useAction();
  const [reason, setReason] = useState('');

  const errors = spec.action === 'submit' ? scheme.checks.filter((c) => c.level === 'error') : [];
  const warnings =
    spec.action === 'submit' ? scheme.checks.filter((c) => c.level === 'warning') : [];
  const blocked = errors.length > 0;
  const reasonMissing = spec.needsReason && reason.trim() === '';

  async function confirm() {
    const ok = await run(
      () =>
        transition({
          id: scheme.id,
          action: spec.action,
          ...(spec.needsReason ? { reason: reason.trim() } : {}),
        }).unwrap(),
      'Переход не выполнен. Обновите карточку и попробуйте ещё раз.',
    );
    if (ok) onClose();
  }

  return (
    <Modal open title={spec.label} onClose={busy ? () => undefined : onClose}>
      <Stack $gap={4}>
        <Hint>{spec.hint}</Hint>
        {blocked && (
          <Notice $tone="danger" role="alert">
            <strong>Передать нельзя — контроль ГОСТ Р 51872 нашёл ошибки:</strong>
            <ChecksList checks={errors} />
          </Notice>
        )}
        {!blocked && warnings.length > 0 && (
          <Notice $tone="warning" role="status">
            <strong>Замечания контроля — передать можно, но лучше поправить:</strong>
            <ChecksList checks={warnings} />
          </Notice>
        )}
        {spec.needsReason && (
          <FieldLabel>
            Причина (обязательно) — её увидит геодезист
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Что исправить в схеме"
              autoFocus
              required
              aria-required="true"
            />
          </FieldLabel>
        )}
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <Row $gap={2}>
          <Button
            type="button"
            $variant={spec.variant === 'danger' ? 'danger' : 'primary'}
            onClick={() => void confirm()}
            disabled={busy || blocked || reasonMissing}
            autoFocus={!spec.needsReason && !blocked}
          >
            {busy ? 'Выполняется…' : spec.label}
          </Button>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
        </Row>
      </Stack>
    </Modal>
  );
}

/** Диалог с обязательной причиной (отклонение заявки, правка съёмки). */
export function ReasonDialog({
  title,
  hint,
  label,
  confirmLabel,
  danger = false,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  hint?: string;
  label: string;
  confirmLabel: string;
  danger?: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const missing = reason.trim() === '';
  return (
    <Modal open title={title} onClose={busy ? () => undefined : onClose}>
      <Stack $gap={4}>
        {hint && <Hint>{hint}</Hint>}
        <FieldLabel>
          {label}
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
            required
            aria-required="true"
          />
        </FieldLabel>
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <Row $gap={2}>
          <Button
            type="button"
            $variant={danger ? 'danger' : 'primary'}
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || missing}
          >
            {busy ? 'Сохранение…' : confirmLabel}
          </Button>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
        </Row>
      </Stack>
    </Modal>
  );
}
